/**
 * SecureEmbed Related Connections
 *
 * Inline component showing related entities for a given query/insight.
 * Displays grouped cards with expandable rows and links to the Connections page.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, Link2, ArrowRight, ChevronDown, ChevronRight, Sparkles,
  FileText, Clock, Network,
} from 'lucide-react';
import { connectionsApi, ConnectionNode, ConnectionGroup } from '../services/api';
import DataSourceBadge from './DataSourceBadge';

interface RelatedConnectionsProps {
  /** The query string to find related entities for */
  query: string;
  /** Max entities to show per group (default 3) */
  maxPerGroup?: number;
  /** Max groups to show (default 3) */
  maxGroups?: number;
  /** Optional className */
  className?: string;
  /** Whether to start collapsed (default false) */
  startCollapsed?: boolean;
  /** Label override (default 'Related Connections') */
  label?: string;
}

const RelatedConnections: React.FC<RelatedConnectionsProps> = ({
  query,
  maxPerGroup = 3,
  maxGroups = 3,
  className = '',
  startCollapsed = false,
  label = 'Related Connections',
}) => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState<ConnectionGroup[]>([]);
  const [totalEdges, setTotalEdges] = useState(0);
  const [totalNodes, setTotalNodes] = useState(0);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(startCollapsed);
  const [fetched, setFetched] = useState(false);
  const [expandedNode, setExpandedNode] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const fetch = useCallback(async () => {
    if (!query.trim() || fetched) return;
    setLoading(true);
    try {
      const resp = await connectionsApi.explore(query, 20);
      setGroups(resp.groups);
      setTotalEdges(resp.totalEdges);
      setTotalNodes(resp.totalNodes);
    } catch {
      // silent — connections are supplementary
    } finally {
      setLoading(false);
      setFetched(true);
    }
  }, [query, fetched]);

  useEffect(() => {
    if (!collapsed && !fetched) fetch();
  }, [collapsed, fetched, fetch]);

  // Reset when query changes
  useEffect(() => {
    setFetched(false);
    setGroups([]);
    setExpandedNode(null);
    setExpandedGroups(new Set());
  }, [query]);

  const toggleGroup = (source: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(source)) next.delete(source);
      else next.add(source);
      return next;
    });
  };

  const getScoreBg = (score: number): string => {
    if (score >= 0.9) return 'bg-green-100 text-green-700';
    if (score >= 0.7) return 'bg-blue-100 text-blue-700';
    if (score >= 0.5) return 'bg-amber-100 text-amber-700';
    return 'bg-gray-100 text-gray-600';
  };

  const openConnectionsPage = () => {
    navigate(`/search?tab=connections&q=${encodeURIComponent(query)}`);
  };

  const toggleNode = (nodeId: string) => {
    setExpandedNode(prev => prev === nodeId ? null : nodeId);
  };

  if (!query.trim()) return null;

  return (
    <div className={`border border-gray-200 rounded-xl overflow-hidden ${className}`}>
      {/* Header — always visible */}
      <button
        onClick={() => { setCollapsed(!collapsed); }}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <Link2 className="w-4 h-4 text-purple-500" />
          {label}
          {totalNodes > 0 && (
            <span className="text-xs text-gray-400 font-normal">
              {totalNodes} entities
            </span>
          )}
          {totalEdges > 0 && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs bg-purple-50 text-purple-600">
              <Sparkles className="w-3 h-3" />
              {totalEdges}
            </span>
          )}
        </span>
        {collapsed
          ? <ChevronRight className="w-4 h-4 text-gray-400" />
          : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>

      {/* Body */}
      {!collapsed && (
        <div className="px-4 py-3 space-y-3">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Finding related entities...
            </div>
          )}

          {!loading && groups.length === 0 && fetched && (
            <p className="text-xs text-gray-400 py-1">No related connections found</p>
          )}

          {groups.slice(0, maxGroups).map((group) => {
            const isGroupExpanded = expandedGroups.has(group.source);
            const visibleNodes = isGroupExpanded ? group.nodes : group.nodes.slice(0, maxPerGroup);
            const hasMore = group.nodes.length > maxPerGroup;
            return (
            <div key={group.source} className="space-y-1.5">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: group.color }} />
                <DataSourceBadge source={group.source} size="sm" />
                <span className="text-xs text-gray-400">{group.nodes.length}</span>
              </div>
              {visibleNodes.map((node: ConnectionNode) => (
                <div key={node.id} className="ml-5">
                  <div
                    onClick={() => toggleNode(node.id)}
                    className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 transition-colors text-sm cursor-pointer"
                    style={{ borderLeft: `3px solid ${group.color}` }}
                  >
                    {expandedNode === node.id
                      ? <ChevronDown className="w-3 h-3 text-gray-400 shrink-0" />
                      : <ChevronRight className="w-3 h-3 text-gray-400 shrink-0" />
                    }
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium shrink-0 ${getScoreBg(node.score)}`}>
                      {(node.score * 100).toFixed(0)}%
                    </span>
                    <span className="text-gray-700 truncate flex-1">{node.label}</span>
                    {node.crossSourceCount > 0 && (
                      <span className="inline-flex items-center gap-0.5 text-xs text-purple-500 shrink-0">
                        <Sparkles className="w-3 h-3" />{node.crossSourceCount}
                      </span>
                    )}
                  </div>
                  {/* Expanded row details */}
                  {expandedNode === node.id && (
                    <div
                      className="ml-5 mt-1 mb-2 p-3 bg-gray-50 rounded-lg border border-gray-100 space-y-2"
                      style={{ borderLeft: `3px solid ${group.color}` }}
                    >
                      {node.content && (
                        <p className="text-xs text-gray-600 leading-relaxed line-clamp-4">
                          <FileText className="w-3 h-3 inline mr-1 text-gray-400" />
                          {node.content}
                        </p>
                      )}
                      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <DataSourceBadge source={node.source} size="sm" />
                        </span>
                        {node.type && node.type !== 'paper' && (
                          <span className="px-1.5 py-0.5 bg-purple-50 text-purple-600 rounded text-xs">
                            {node.type.replace(/_/g, ' ')}
                          </span>
                        )}
                        {node.timestamp && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(node.timestamp).toLocaleDateString()}
                          </span>
                        )}
                        {node.region && (
                          <span className="text-gray-400">{node.region}</span>
                        )}
                      </div>
                      {node.crossSourceCount > 0 && (
                        <p className="text-xs text-purple-600">
                          <Sparkles className="w-3 h-3 inline mr-0.5" />
                          Connected to {node.crossSourceCount} record{node.crossSourceCount !== 1 ? 's' : ''} in other datasources
                        </p>
                      )}
                      {/* Navigate to graph view for this entity */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/search?tab=connections&q=${encodeURIComponent(node.label)}`);
                        }}
                        className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 transition-colors pt-1"
                      >
                        <Network className="w-3 h-3" />
                        View in relationship graph
                      </button>
                    </div>
                  )}
                </div>
              ))}
              {hasMore && !isGroupExpanded && (
                <button
                  onClick={() => toggleGroup(group.source)}
                  className="ml-5 text-xs text-purple-600 hover:text-purple-800 transition-colors"
                >
                  +{group.nodes.length - maxPerGroup} more — show all
                </button>
              )}
              {hasMore && isGroupExpanded && (
                <button
                  onClick={() => toggleGroup(group.source)}
                  className="ml-5 text-xs text-gray-400 hover:text-gray-600 transition-colors"
                >
                  Show fewer
                </button>
              )}
            </div>
            );
          })}

          {groups.length > maxGroups && (
            <p className="text-xs text-gray-400">+{groups.length - maxGroups} more sources</p>
          )}

          {/* Link to full Connections page */}
          {totalNodes > 0 && (
            <button
              onClick={openConnectionsPage}
              className="flex items-center gap-1.5 text-xs font-medium text-purple-600 hover:text-purple-800 transition-colors pt-1"
            >
              Explore all connections
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default RelatedConnections;
